// BUSCA POR IA — Fase 4 do INC-0018 (front dos sites).
//
// Fonte ÚNICA dos parâmetros que SÓ a busca por IA põe na URL da /imoveis. Os dois lados usam
// este arquivo: o hero (components/pieces/welcome-ia/useBuscaIa) MONTA a URL e a listagem
// (pages/imoveis) LÊ, repassa para a busca e reconstrói ao paginar/ordenar. Sem nenhum destes
// parâmetros na URL nada daqui entra em ação — a listagem dos ~300 sites (filtro manual, SEO,
// heros sem IA) segue byte a byte como era.
//
//   ia=1                   a URL veio da busca por IA (a listagem procura a faixa do aviso)
//   dormitorios_min=2      quartos/suítes/banheiros/vagas como MÍNIMO: "2 suítes" = 2 ou mais.
//   suites_min, banheiros_min, vagas_min    (os `dormitorios=`/`suites=` de sempre continuam
//                          EXATOS — são os botões 1·2·3·4·5+ do filtro lateral)
//   perto=<lat>,<lng>      ponto do "perto de…" → a lista vem do mais perto para o mais longe
//   perto_rotulo=<texto>   nome do lugar, para "Mais próximos de Paraíso"
//   ordem=asc-distancia    a ordem "Mais próximos" (só existe com `perto`)
import type { BuscaIaFilters } from '../services/aiSearch';
import { removerAcentos, segmentoFiltroSeguro } from './FunctionsGlobals';

export const PARAM_IA = 'ia';
export const PARAM_PERTO = 'perto';
export const PARAM_PERTO_ROTULO = 'perto_rotulo';
export const ORDEM_DISTANCIA = 'asc-distancia';

export type CampoMinimoIa = 'dormitorios_min' | 'suites_min' | 'banheiros_min' | 'garagems_min';
export type MinimosBuscaIa = Record<CampoMinimoIa, number>;

// `campo` = nome do filtro no estado da listagem E no imoveis_busca; `param` = nome na URL.
export const MINIMOS_BUSCA_IA: ReadonlyArray<{
    campo: CampoMinimoIa;
    param: string;
    singular: string;
    plural: string;
}> = [
    { campo: 'dormitorios_min', param: 'dormitorios_min', singular: 'quarto', plural: 'quartos' },
    { campo: 'suites_min', param: 'suites_min', singular: 'suíte', plural: 'suítes' },
    { campo: 'banheiros_min', param: 'banheiros_min', singular: 'banheiro', plural: 'banheiros' },
    { campo: 'garagems_min', param: 'vagas_min', singular: 'vaga', plural: 'vagas' },
];

export const minimosVazios = (): MinimosBuscaIa => ({
    dormitorios_min: 0,
    suites_min: 0,
    banheiros_min: 0,
    garagems_min: 0,
});

export interface PontoBuscaIa {
    lat: number;
    lng: number;
}

export interface ParametrosBuscaIa {
    ia: boolean;
    minimos: MinimosBuscaIa;
    perto: PontoBuscaIa | null;
    pertoRotulo: string;
}

const primeiro = (valor: unknown): unknown => (Array.isArray(valor) ? valor[0] : valor);

// Mínimo de cômodos: inteiro positivo com teto (ninguém busca "25 suítes ou mais"; o teto só
// impede que um número absurdo colado na URL vá parar na consulta).
const inteiroPositivo = (valor: unknown, teto = 20): number => {
    const n = Number(primeiro(valor));
    return Number.isFinite(n) && n > 0 ? Math.min(Math.floor(n), teto) : 0;
};

const positivo = (valor: unknown): number => {
    const n = Number(valor);
    return Number.isFinite(n) && n > 0 ? Math.floor(n) : 0;
};

export function lerPonto(lat: unknown, lng: unknown): PontoBuscaIa | null {
    if (lat === null || lat === undefined || lat === '' || lng === null || lng === undefined || lng === '') {
        return null;
    }
    const la = Number(lat);
    const lo = Number(lng);
    if (!Number.isFinite(la) || !Number.isFinite(lo)) return null;
    if (Math.abs(la) > 90 || Math.abs(lo) > 180 || (la === 0 && lo === 0)) return null;
    return { lat: la, lng: lo };
}

// `perto=-23.574,-46.641` → ponto (ou null se malformado / fora do globo)
export function lerPerto(valor: unknown): PontoBuscaIa | null {
    const bruto = String(primeiro(valor) ?? '').trim();
    if (!bruto) return null;
    const partes = bruto.split(',');
    if (partes.length !== 2) return null;
    return lerPonto(partes[0].trim(), partes[1].trim());
}

// 6 casas (~10 cm) e sem zeros à direita: o MESMO texto na URL do hero e na reconstruída.
const arredonda6 = (n: number): number => Number(n.toFixed(6));
export const formatarPerto = (p: PontoBuscaIa): string => `${arredonda6(p.lat)},${arredonda6(p.lng)}`;

export function limparRotulo(valor: unknown): string {
    return String(primeiro(valor) ?? '')
        .replace(/[ -]/g, ' ')
        .replace(/\s+/g, ' ')
        .trim()
        .slice(0, 60);
}

// A listagem passa a URL inteira por toLowerCase ao reconstruí-la; o rótulo que voltou em
// minúsculas ("vila mariana") é mostrado com as iniciais em maiúscula ("Vila Mariana").
const PALAVRAS_MINUSCULAS = new Set(['de', 'da', 'do', 'das', 'dos', 'e', 'em', 'na', 'no']);
export function rotuloLegivel(valor: unknown): string {
    const texto = limparRotulo(valor);
    if (!texto || texto !== texto.toLowerCase()) return texto;
    return texto
        .split(' ')
        .map((p, i) => (i > 0 && PALAVRAS_MINUSCULAS.has(p) ? p : p.charAt(0).toUpperCase() + p.slice(1)))
        .join(' ');
}

export const rotuloOrdemDistancia = (rotulo: unknown): string => {
    const lugar = rotuloLegivel(rotulo);
    return lugar ? `Mais próximos de ${lugar}` : 'Mais próximos';
};

export const rotuloMinimoIa = (campo: CampoMinimoIa, valor: number): string => {
    const m = MINIMOS_BUSCA_IA.find((x) => x.campo === campo);
    if (!m || !valor) return '';
    return `${valor}+ ${valor === 1 ? m.singular : m.plural}`;
};

// Lê os parâmetros da IA a partir de um "leitor" de query (router.query com fallback no asPath).
export function lerParametrosBuscaIa(ler: (chave: string) => unknown): ParametrosBuscaIa {
    const minimos = minimosVazios();
    for (const m of MINIMOS_BUSCA_IA) minimos[m.campo] = inteiroPositivo(ler(m.param));
    const perto = lerPerto(ler(PARAM_PERTO));
    return {
        ia: String(primeiro(ler(PARAM_IA)) ?? '') === '1',
        minimos,
        perto,
        pertoRotulo: perto ? limparRotulo(ler(PARAM_PERTO_ROTULO)) : '',
    };
}

// Leitor de query para quem não tem o router (testes, chave da faixa): query do Next primeiro,
// senão a query string do caminho.
export function leitorDeQuery(query: Record<string, unknown> | null | undefined, caminho?: string) {
    const doCaminho = new URLSearchParams(String(caminho || '').split('#')[0].split('?')[1] || '');
    return (chave: string): unknown => {
        const valor = query?.[chave];
        if (Array.isArray(valor)) return valor[0];
        if (valor !== undefined && valor !== null && valor !== '') return valor;
        const doPath = doCaminho.get(chave);
        return doPath !== null ? doPath : undefined;
    };
}

export const temMinimosIa = (m?: Partial<MinimosBuscaIa> | null): boolean =>
    !!m && MINIMOS_BUSCA_IA.some((x) => inteiroPositivo(m[x.campo]) > 0);

export const buscaIaAtiva = (p?: ParametrosBuscaIa | null): boolean =>
    !!p && (p.ia || temMinimosIa(p.minimos) || !!p.perto);

export const parametrosBuscaIaVazios = (): ParametrosBuscaIa => ({
    ia: false,
    minimos: minimosVazios(),
    perto: null,
    pertoRotulo: '',
});

// O filtro EXATO escolhido na lateral (estado `filters` da listagem: dormitorios_min=2 vindo de
// `dormitorios=2`) vence o mínimo da IA daquele campo — nunca vão os dois para a consulta.
export function minimosIaEfetivos(minimos: Partial<MinimosBuscaIa> | null | undefined, exatos: any): MinimosBuscaIa {
    const efetivos = minimosVazios();
    if (!minimos) return efetivos;
    for (const m of MINIMOS_BUSCA_IA) {
        const exato = Number(exatos?.[m.campo]) || 0;
        efetivos[m.campo] = exato > 0 ? 0 : inteiroPositivo(minimos[m.campo]);
    }
    return efetivos;
}

// Parâmetros da IA para ACRESCENTAR à URL (sem "?"/"&" na frente). É a MESMA função no hero
// (URL nova) e na listagem (URL reconstruída ao paginar/ordenar) — a URL não "perde" a IA.
export function queryBuscaIa(p: {
    ia?: boolean;
    minimos?: Partial<MinimosBuscaIa> | null;
    perto?: PontoBuscaIa | null;
    pertoRotulo?: string;
}): string {
    const partes: string[] = [];
    if (p.ia) partes.push(`${PARAM_IA}=1`);
    for (const m of MINIMOS_BUSCA_IA) {
        const valor = inteiroPositivo(p.minimos?.[m.campo]);
        if (valor) partes.push(`${m.param}=${valor}`);
    }
    if (p.perto) {
        partes.push(`${PARAM_PERTO}=${formatarPerto(p.perto)}`);
        const rotulo = limparRotulo(p.pertoRotulo);
        if (rotulo) partes.push(`${PARAM_PERTO_ROTULO}=${encodeURIComponent(rotulo)}`);
    }
    return partes.join('&');
}

export function acrescentarQuery(url: string, query: string): string {
    if (!query) return url;
    const [semHash, ...hash] = String(url || '').split('#');
    const separador = !semHash.includes('?') ? '?' : /[?&]$/.test(semHash) ? '' : '&';
    return `${semHash}${separador}${query}${hash.length ? `#${hash.join('#')}` : ''}`;
}

// Args extras do imoveis_busca / imoveis (mapa) na busca por IA. Sem extras = '' — a query da
// listagem sai IDÊNTICA à de sempre. Mínimo da IA só vai no campo SEM filtro exato (senão o
// GraphQL recusaria o argumento repetido), e o ponto só vai quando a ordem é a de distância.
export interface ExtrasBuscaIa {
    minimos?: Partial<MinimosBuscaIa> | null;
    perto?: PontoBuscaIa | null;
}
export function argsGraphqlBuscaIa(
    extras: ExtrasBuscaIa | null | undefined,
    exatos: Partial<Record<CampoMinimoIa, unknown>>
): string {
    if (!extras) return '';
    let args = '';
    for (const m of MINIMOS_BUSCA_IA) {
        const valor = inteiroPositivo(extras.minimos?.[m.campo]);
        if (valor && !(Number(exatos?.[m.campo]) > 0)) args += `,${m.campo}: ${valor}`;
    }
    const ponto = extras.perto ? lerPonto(extras.perto.lat, extras.perto.lng) : null;
    if (ponto) args += `,perto_lat: ${ponto.lat.toFixed(6)},perto_lng: ${ponto.lng.toFixed(6)}`;
    return args;
}

// PREÇO NO TOGGLE "ALUGAR": sem transação na frase a IA devolve venda=1 e o preço em
// preco_venda_*; quem clicou em Alugar ficava SEM preço. Vale o par da transação escolhida;
// vazio ele, vale o outro par (o número que a pessoa digitou).
export function precoPelaTransacao(filters: BuscaIaFilters | null | undefined, transacao: string) {
    const venda = { min: positivo(filters?.preco_venda_min), max: positivo(filters?.preco_venda_max) };
    const locacao = { min: positivo(filters?.preco_locacao_min), max: positivo(filters?.preco_locacao_max) };
    const esperado = transacao === 'venda' ? venda : locacao;
    const outro = transacao === 'venda' ? locacao : venda;
    return esperado.min || esperado.max ? esperado : outro;
}

const limitarPreco = (valor: number, limites: any): number => {
    const min = Number(limites?.min);
    const max = Number(limites?.max);
    if (!Number.isFinite(min) || !Number.isFinite(max) || max <= 0) return valor;
    return Math.min(Math.max(valor, min), max);
};

const unicos = (lista: string[]): string[] => Array.from(new Set(lista.filter(Boolean)));

export interface EntradaCaminhoBuscaIa {
    filters: BuscaIaFilters;
    transacao: string;
    /** cidade já resolvida (allCities) — sem ela o bairro não entra (a listagem só casa bairro dentro da cidade) */
    cidade?: { label?: unknown } | null;
    /** nomes dos bairros, na ordem da lista do site */
    bairros?: unknown[];
    /** nomes dos tipos */
    tipos?: unknown[];
    /** limites de preço do tenant para a transação (valores.precos_venda/locacao[0]) */
    limitesPreco?: any;
    /** a URL vai com `ordem=asc-distancia` (ponto do "perto"): não põe o `ordem=desc-inclusao` da busca vazia */
    ordemDistancia?: boolean;
}

// Caminho /imoveis/<transacao>/<cidade>/<bairro>/-/<tipo>?min=&max=&area_min=&area_max= que a
// listagem REALMENTE entende (ver pages/imoveis/index.tsx), sem os parâmetros da IA (esses vão
// por `queryBuscaIa`, depois do finalizeUrl do hero, que passa tudo por toLowerCase).
export function montarCaminhoBuscaIa(entrada: EntradaCaminhoBuscaIa): {
    caminho: string;
    vazio: boolean;
    minimos: MinimosBuscaIa;
} {
    const { filters, transacao } = entrada;
    const citySegment = entrada.cidade ? segmentoFiltroSeguro(entrada.cidade.label) : '';
    // Nome repetido no cadastro ("Apartamento" 3×) virava "apartamento+apartamento+…" na URL.
    const districtSegment = citySegment ? unicos((entrada.bairros || []).map(segmentoFiltroSeguro)).join('+') : '';
    const typeSegment = unicos((entrada.tipos || []).map(segmentoFiltroSeguro)).join('+');

    const params = new URLSearchParams();
    // preço: a lista aplica min/max NA transação ativa e valida contra os limites do tenant
    // (resolvePriceRangeFromQuery) — limita aqui para o filtro não ser descartado.
    const preco = precoPelaTransacao(filters, transacao);
    if (preco.min || preco.max) {
        const limites = entrada.limitesPreco;
        const min = preco.min ? limitarPreco(preco.min, limites) : Number(limites?.min) || 0;
        const max = preco.max ? limitarPreco(preco.max, limites) : Number(limites?.max) || 0;
        if (max > 0) {
            params.set('min', String(min));
            params.set('max', String(max));
        }
    }
    const areaMin = positivo(filters?.metragem_min);
    const areaMax = positivo(filters?.metragem_max);
    if (areaMin || areaMax) {
        params.set('area_min', String(areaMin));
        params.set('area_max', String(areaMax));
    }

    const minimos = minimosVazios();
    for (const m of MINIMOS_BUSCA_IA) minimos[m.campo] = inteiroPositivo(filters?.[m.campo]);

    const vazio =
        !citySegment && !typeSegment && !districtSegment && params.toString() === '' && !temMinimosIa(minimos);
    let caminho = `/imoveis/${transacao}/${citySegment || '-'}/${districtSegment || '-'}/-/${typeSegment || '-'}`;
    if (vazio && !entrada.ordemDistancia) caminho = `${caminho}?ordem=desc-inclusao`;
    else if (params.toString()) caminho = `${caminho}?${params.toString()}`;
    return { caminho, vazio, minimos };
}

// ─── Faixa do aviso (sessionStorage) ────────────────────────────────────────────────────────
// O hero guarda o que a IA avisou; a listagem mostra a faixa. A chave sai do CAMINHO + só os
// parâmetros da IA (ordenados e normalizados): a URL que a listagem reconstrói ao paginar
// (com pagination/ordem/min/max e tipos repetidos) cai na MESMA chave da URL que o hero gerou.
export interface OutraCidadeFaixa {
    nome: string;
    n: number;
    url: string;
}
export interface DadosFaixaBuscaIa {
    aviso: string;
    interpretacao: string;
    lugar: string;
    outras_cidades: OutraCidadeFaixa[];
    termos_ignorados: string[];
}

const PREFIXO_FAIXA = 'busca_ia:faixa:';

export function chaveFaixaBuscaIa(url: string): string {
    const [semHash] = String(url || '').split('#');
    const [caminho = '', query = ''] = semHash.split('?');
    let decodificado = caminho;
    try {
        decodificado = decodeURIComponent(caminho);
    } catch {
        // "%" solto no caminho: usa como veio
    }
    const segmentos = removerAcentos(decodificado)
        .toLowerCase()
        .split('/')
        .filter(Boolean)
        .map((seg) => unicos(seg.split('+')).sort().join('+'));
    const p = lerParametrosBuscaIa(leitorDeQuery(null, `?${query}`));
    return `${PREFIXO_FAIXA}/${segmentos.join('/')}?${queryBuscaIa({ ia: p.ia, minimos: p.minimos, perto: p.perto })}`;
}

const texto = (valor: unknown, max: number): string =>
    String(valor ?? '')
        .replace(/\s+/g, ' ')
        .trim()
        .slice(0, max);

export function normalizarFaixaBuscaIa(bruto: any): DadosFaixaBuscaIa | null {
    if (!bruto || typeof bruto !== 'object') return null;
    const outras = Array.isArray(bruto.outras_cidades) ? bruto.outras_cidades : [];
    return {
        aviso: texto(bruto.aviso, 300),
        interpretacao: texto(bruto.interpretacao, 300),
        lugar: texto(bruto.lugar, 60),
        outras_cidades: outras
            .map((o: any) => ({ nome: texto(o?.nome, 60), n: positivo(o?.n), url: String(o?.url ?? '') }))
            // só link interno da listagem — nada de "javascript:" vindo do storage
            .filter((o: OutraCidadeFaixa) => o.nome && o.url.startsWith('/imoveis/'))
            .slice(0, 4),
        termos_ignorados: (Array.isArray(bruto.termos_ignorados) ? bruto.termos_ignorados : [])
            .map((t: unknown) => texto(t, 40))
            .filter(Boolean)
            .slice(0, 5),
    };
}

export const faixaTemConteudo = (d?: DadosFaixaBuscaIa | null): boolean =>
    !!d && (!!d.aviso || d.outras_cidades.length > 0);

const sessionStorageOuNull = (): Storage | null => {
    try {
        return typeof window !== 'undefined' && window.sessionStorage ? window.sessionStorage : null;
    } catch {
        return null; // Safari privado / storage bloqueado: a faixa só não aparece
    }
};

// Grava (ou apaga, com `dados` null) a faixa da URL gerada. Sempre sobrescreve: uma busca nova
// que caia na mesma chave nunca herda o aviso da anterior.
export function salvarFaixaBuscaIa(url: string, dados: DadosFaixaBuscaIa | null): void {
    const storage = sessionStorageOuNull();
    if (!storage) return;
    try {
        const chave = chaveFaixaBuscaIa(url);
        const normalizado = dados ? normalizarFaixaBuscaIa(dados) : null;
        if (!normalizado) storage.removeItem(chave);
        else storage.setItem(chave, JSON.stringify(normalizado));
    } catch {
        // cota cheia / storage indisponível: ignora
    }
}

export function lerFaixaBuscaIa(url: string): DadosFaixaBuscaIa | null {
    const storage = sessionStorageOuNull();
    if (!storage) return null;
    try {
        const bruto = storage.getItem(chaveFaixaBuscaIa(url));
        return bruto ? normalizarFaixaBuscaIa(JSON.parse(bruto)) : null;
    } catch {
        return null;
    }
}

// ─── Campos v2 do buscaIa (JSON/objetos do GraphQL) ─────────────────────────────────────────
export function lerPontoReferencia(bruto: any): (PontoBuscaIa & { rotulo: string }) | null {
    if (!bruto || typeof bruto !== 'object') return null;
    const ponto = lerPonto(bruto.lat, bruto.lng);
    return ponto ? { ...ponto, rotulo: limparRotulo(bruto.rotulo) } : null;
}

export function normalizarOutrasCidades(
    bruto: unknown
): Array<{ id_cidade: number; nome: string; id_bairro: number[]; n: number }> {
    if (!Array.isArray(bruto)) return [];
    return bruto
        .map((o: any) => ({
            id_cidade: positivo(o?.id_cidade),
            nome: texto(o?.nome, 60),
            id_bairro: (Array.isArray(o?.id_bairro) ? o.id_bairro : []).map(positivo).filter(Boolean),
            n: positivo(o?.n),
        }))
        .filter((o) => o.id_cidade && o.nome)
        .slice(0, 4);
}
